import { useMemo } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Section } from '@/components/ds/Section'
import { Button } from '@/components/ui/Button'
import type { RecoveryExperiment } from '@/api/endpoints/channelRecovery'
import styles from '@/features/recovery/pages/RecoveryPage/RecoveryPage.module.scss'

const CONTROL_COLORS = [
  'var(--fg-subtle)',
  'var(--brand-ember)',
  'var(--brand-chartreuse)',
  'var(--fg-muted)',
]

export interface ExperimentSectionProps {
  experiment: RecoveryExperiment | undefined
  controlBusy: boolean
  onToggleControl: (mark: boolean) => void
}

function fmt(n: number | null | undefined, digits = 0): string {
  return n === null || n === undefined ? '—' : n.toFixed(digits)
}

/**
 * Views diárias do canal antes e depois do primeiro lote, com os controles.
 * Descritivo: sem teste estatístico.
 */
export function ExperimentSection({
  experiment,
  controlBusy,
  onToggleControl,
}: ExperimentSectionProps) {
  const indexed = experiment?.reference_day != null
  const data = useMemo(() => {
    if (!experiment) return []
    const rows = new Map<string, Record<string, number | string | null>>()
    const row = (day: string) => {
      let r = rows.get(day)
      if (!r) {
        r = { day }
        rows.set(day, r)
      }
      return r
    }
    for (const p of experiment.series) row(p.day)['canal'] = indexed ? p.indexed : p.views
    for (const c of experiment.controls)
      for (const p of c.series) row(p.day)[c.channel_slug] = indexed ? p.indexed : p.views
    return [...rows.values()].sort((a, b) => String(a['day']).localeCompare(String(b['day'])))
  }, [experiment, indexed])

  const summary = experiment?.summary
  return (
    <Section
      title="Experimento"
      description="Views diárias do Analytics (dia do Pacífico; o Analytics atrasa uns 2 dias). Com lote, cada linha é indexada pela média dos 14 dias antes (= 100). É descritivo: não prova causa."
      actions={
        experiment ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={controlBusy}
            onClick={() => {
              onToggleControl(!experiment.is_control)
            }}
          >
            {experiment.is_control ? 'Desmarcar controle' : 'Marcar como controle'}
          </Button>
        ) : null
      }
    >
      {!experiment ? (
        <p className={styles.muted}>Carregando…</p>
      ) : data.length === 0 ? (
        <p className={styles.muted}>
          Sem views diárias ainda (o canal precisa estar conectado ao YouTube).
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="day"
                stroke="var(--fg-subtle)"
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="var(--fg-subtle)"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                width={42}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {experiment.batches.map((b) => (
                <ReferenceLine
                  key={b.id}
                  x={b.day}
                  stroke="var(--brand-ignite)"
                  strokeDasharray="3 3"
                />
              ))}
              <Line
                type="monotone"
                dataKey="canal"
                stroke="var(--brand-ignite)"
                strokeWidth={2}
                dot={false}
              />
              {experiment.controls.map((c, i) => (
                <Line
                  key={c.channel_slug}
                  type="monotone"
                  dataKey={c.channel_slug}
                  stroke={CONTROL_COLORS[i % CONTROL_COLORS.length] ?? 'var(--fg-subtle)'}
                  strokeWidth={1.5}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          {indexed && summary ? (
            <p className={styles.summary}>
              Média diária: {fmt(summary.before_avg)} antes · {fmt(summary.after_avg)} depois ·
              razão {fmt(summary.ratio, 2)}
              {summary.control_ratio !== null
                ? ` · controles ${fmt(summary.control_ratio, 2)}`
                : ''}
            </p>
          ) : (
            <p className={styles.muted}>Sem lote ainda: a referência é o primeiro lote do canal.</p>
          )}
        </>
      )}
    </Section>
  )
}
